import { useLayoutEffect, type RefObject } from "react";

/**
 * Sizes the case's chat and side column to the space left below the case header, so the whole
 * chat (with its message box) is visible without scrolling the page. Sets `--case-pane` in px.
 */
export function useFitToViewport(ref: RefObject<HTMLElement | null>, deps: readonly unknown[] = []) {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => {
      // Distance from the top of the page (not of the current scroll) to the chat area.
      const top = element.getBoundingClientRect().top + window.scrollY;
      const height = Math.max(420, Math.round(window.innerHeight - top - 32));
      element.style.setProperty("--case-pane", `${height}px`);
    };
    update();
    window.addEventListener("resize", update);
    // The header above can grow or shrink (edited facts, notices): recompute when the page does.
    const page = element.parentElement;
    const observer = typeof ResizeObserver !== "undefined" && page ? new ResizeObserver(update) : null;
    if (observer && page) observer.observe(page);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);
}
